import API_BASE_URL from '../config';
import React, { useState } from "react";
import { useNavigate } from 'react-router-dom';

const ARTICLE_TYPES = [
    { value: 'suit', label: '👗 Suit' },
    { value: 'jutti', label: '👡 Jutti' },
    { value: 'jewellery', label: '💎 Jewellery' },
    { value: 'bags', label: '👜 Bags' },
];

const AddArticle = () => {
    const navigate = useNavigate();

    // Core States
    const [articleType, setArticleType] = useState('suit');
    const [isKidsArticle, setIsKidsArticle] = useState(false);
    const [isPublic, setIsPublic] = useState(false);
    const [isFeatured, setIsFeatured] = useState(false);
    const [photos, setPhotos] = useState([]); // Array of Base64 strings
    const [isDragging, setIsDragging] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [promotionEntries, setPromotionEntries] = useState([
        { cost: "", date: "", description: "", from: "" }
    ]);

    // --- NEW: SIZES STATE (used for jutti) ---
    const [sizes, setSizes] = useState([]);
    const [sizeInput, setSizeInput] = useState("");

    const addSize = () => {
        const val = sizeInput.trim();
        if (!val) return;
        if (sizes.includes(val)) {
            setSizeInput("");
            return;
        }
        setSizes(prev => [...prev, val]);
        setSizeInput("");
    };

    const removeSize = (val) => {
        setSizes(prev => prev.filter(s => s !== val));
    };

    const handleSizeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addSize();
        }
    };

    // Handle multiple image conversion
    const handleImageChange = (files) => {
        if (!files) return;
        
        const fileArray = Array.from(files);
        // Limit to 11 photos
        if (photos.length + fileArray.length > 11) {
            alert("Maximum 11 photos allowed per article.");
            return;
        }

        fileArray.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotos(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removePhoto = (index) => {
        setPhotos(photos.filter((_, i) => i !== index));
    };

    // --- NEW: Move a photo to index 0 (website thumbnail) ---
    const setAsThumbnail = (index) => {
        if (index === 0) return;
        setPhotos(prev => {
            const updated = [...prev];
            const [selected] = updated.splice(index, 1);
            updated.unshift(selected);
            return updated;
        });
    };

    // Drag and Drop handlers
    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };
    const onDragLeave = () => setIsDragging(false);
    const onDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleImageChange(e.dataTransfer.files);
    };

    const handlePromotionChange = (index, field, value) => {
        const updatedEntries = [...promotionEntries];
        updatedEntries[index][field] = value;
        setPromotionEntries(updatedEntries);
    };

    const addPromotionField = () => {
        setPromotionEntries([...promotionEntries, { cost: "", date: "", description: "", from: "" }]);
    };

    const removePromotionField = (index) => {
        const updatedEntries = promotionEntries.filter((_, i) => i !== index);
        setPromotionEntries(updatedEntries.length ? updatedEntries : [{ cost: "", date: "", description: "", from: "" }]);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setUpdating(true)

        const formData = new FormData(event.target);
        const data = Object.fromEntries(formData.entries());

        // Append custom states
        data.articleType = articleType;
        data.articletype = isKidsArticle; // legacy field, kept for compatibility
        data.isPublic = isPublic;
        data.isFeatured = isFeatured;
        data.photos = photos;
        data.promotions = promotionEntries.filter(p => p.cost || p.date || p.from);

        // --- NEW: Only attach sizes for jutti ---
        if (articleType === 'jutti') {
            data.sizes = sizes;
        }

        // --- Clean up fields that don't apply to non-suit types ---
        if (articleType !== 'suit') {
            delete data.fabricType;
            delete data.dupattaType;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/addarticlea`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });

            const result = await response.json();

            if (response.ok) {
                alert("✅ Success: " + result.message);
                navigate('/articles');
                setUpdating(false);
            } else {
                alert("❌ Error: " + (result.message || "Something went wrong"));
                setUpdating(false);
            }
        } catch (error) {
            alert("❌ Server Error: Could not connect to the backend.");
            setUpdating(false);
        }
    };

    // --- Helpers to know what to show based on articleType ---
    const showFabricDupattaSeason = articleType === 'suit';
    const showSeasonOnly = articleType === 'jewellery' || articleType === 'bags';
    const showSizes = articleType === 'jutti';
    const showFullPricing = articleType === 'suit';

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    {/* PAGE HEADER */}
                    <div className="d-flex align-items-center mb-4 flex-wrap gap-2">
                        <h2 className="fw-bold mb-0">Add New <span className="text-primary">Article</span></h2>

                        <div className="ms-auto d-flex align-items-center">
                            <label 
                                className="fw-bold me-2 mb-0 user-select-none" 
                                style={{ cursor: 'pointer', color: isKidsArticle ? '#0d6efd' : '#6c757d' }}
                            >
                                {isKidsArticle ? "👶 Kids Article" : "Standard Article"}
                            </label>
                            <div className="form-check form-switch mb-0">
                                <input 
                                    className="form-check-input shadow-none" 
                                    type="checkbox" 
                                    role="switch" 
                                    checked={isKidsArticle}
                                    onChange={(e) => setIsKidsArticle(e.target.checked)}
                                    style={{ width: '2.5em', height: '1.25em', cursor: 'pointer' }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* --- NEW: ARTICLE TYPE SELECTOR --- */}
                    <div className="card shadow-sm border-0 p-3 mb-3">
                        <h6 className="fw-bold text-secondary mb-2">🗂️ Article Type</h6>
                        <div className="d-flex flex-wrap gap-2">
                            {ARTICLE_TYPES.map(t => (
                                <button
                                    key={t.value}
                                    type="button"
                                    className={`btn ${articleType === t.value ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setArticleType(t.value)}
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        {/* Hidden input so articleType is also part of FormData if needed */}
                        <input type="hidden" name="articleType" value={articleType} />
                    </div>

                    <div className="card shadow-sm border-0 p-4">
                        <form onSubmit={handleSubmit}>
                            
                            {/* MEDIA SECTION */}
                            <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">🖼️ Photos ({photos.length}/11)</h5>
                            <div className="mb-4">
                                <div 
                                    onDragOver={onDragOver}
                                    onDragLeave={onDragLeave}
                                    onDrop={onDrop}
                                    className={`border border-2 rounded-3 d-flex flex-column align-items-center justify-content-center p-4 transition-all ${isDragging ? 'border-primary bg-light' : 'border-dashed text-muted bg-white'}`}
                                    style={{ borderStyle: 'dashed', minHeight: '120px', cursor: 'pointer' }}
                                    onClick={() => document.getElementById('imageInput').click()}
                                >
                                    <i className="bi bi-images fs-2"></i>
                                    <p className="mb-0">Drag & Drop Multiple Images or Click</p>
                                    <input 
                                        type="file" 
                                        id="imageInput" 
                                        className="d-none" 
                                        multiple 
                                        accept="image/*" 
                                        onChange={(e) => handleImageChange(e.target.files)} 
                                    />
                                </div>

                                {/* PREVIEW GRID */}
                                {photos.length > 0 && (
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        {photos.map((p, i) => (
                                            <div key={i} className="position-relative">
                                                <img
                                                    src={p}
                                                    alt="preview"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        objectFit: 'cover',
                                                        borderRadius: '8px',
                                                        border: i === 0 ? '3px solid #0d6efd' : '1px solid #dee2e6'
                                                    }}
                                                />
                                                {i === 0 && (
                                                    <span
                                                        className="badge bg-primary position-absolute top-0 start-0"
                                                        style={{ fontSize: '9px', transform: 'translate(-4px,-4px)' }}
                                                    >
                                                        Thumbnail
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); removePhoto(i); }}
                                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 py-0 px-1"
                                                    style={{ fontSize: '10px' }}
                                                >✖</button>
                                                {i !== 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); setAsThumbnail(i); }}
                                                        className="btn btn-primary btn-sm position-absolute bottom-0 start-0 py-0 px-1 w-100"
                                                        style={{ fontSize: '9px', borderRadius: '0 0 8px 8px' }}
                                                        title="Set as website thumbnail"
                                                    >
                                                        Set as 1st
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <small className="text-muted d-block mt-2">
                                    The first photo (highlighted in blue) is shown as the thumbnail on the website. Click "Set as 1st" on any photo to make it the thumbnail.
                                </small>
                            </div>

                            {/* BASIC INFO */}
                            <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">📋 Details & Settings</h5>
                            <div className="row g-3 mb-4">
                                <div className="col-12">
                                    <div className="form-floating">
                                        <input className="form-control bg-light border-0" name="name" placeholder="Name" required />
                                        <label>Article Name (Auto-slug)</label>
                                    </div>
                                </div>
                                <div className="col-12">
                                    <div className="form-floating">
                                        <textarea className="form-control bg-light border-0" name="description" placeholder="Public Description" style={{ height: '100px' }}></textarea>
                                        <label>Public Description</label>
                                    </div>
                                </div>
                            </div>

                            {/* CATEGORIZATION - varies by article type */}
                            <div className="row g-3 mb-4">
                                {/* Category: Suit gets Casual/Festive (existing default), Jewellery/Bags get Casual/Festive too. Jutti gets Casual/Festive as well */}
                                <div className={showFabricDupattaSeason ? "col-md-3" : "col-md-4"}>
                                    <div className="form-floating">
                                        <select className="form-select bg-light border-0" name="category" defaultValue="Casual">
                                            <option value="Casual">Casual</option>
                                            <option value="Festive">Festive</option>
                                        </select>
                                        <label>Category</label>
                                    </div>
                                </div>

                                {/* Fabric Type & Dupatta Type - SUIT ONLY */}
                                {showFabricDupattaSeason && (
                                    <>
                                        <div className="col-md-3">
                                            <div className="form-floating">
                                                <input className="form-control bg-light border-0" name="fabricType" placeholder="Fabric" />
                                                <label>Fabric Type</label>
                                            </div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-floating">
                                                <input className="form-control bg-light border-0" name="dupattaType" placeholder="Dupatta" />
                                                <label>Dupatta Type</label>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Season - SUIT and JEWELLERY/BAGS (jutti has no season per spec) */}
                                {(showFabricDupattaSeason || showSeasonOnly) && (
                                    <div className={showFabricDupattaSeason ? "col-md-3" : "col-md-4"}>
                                        <div className="form-floating">
                                            <select className="form-select bg-light border-0" name="season" defaultValue="Winter">
                                                <option value="Winter">Winter</option>
                                                <option value="Summer">Summer</option>
                                            </select>
                                            <label>Season</label>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* --- NEW: SIZES SECTION - JUTTI ONLY --- */}
                            {showSizes && (
                                <div className="mb-4">
                                    <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">📏 Available Sizes</h5>
                                    <div className="d-flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            className="form-control bg-light border-0"
                                            placeholder="e.g. 6, 7, 8, 9..."
                                            value={sizeInput}
                                            onChange={(e) => setSizeInput(e.target.value)}
                                            onKeyDown={handleSizeKeyDown}
                                        />
                                        <button type="button" className="btn btn-outline-primary" onClick={addSize}>+ Add Size</button>
                                    </div>
                                    {sizes.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2">
                                            {sizes.map((s) => (
                                                <span key={s} className="badge bg-secondary d-flex align-items-center gap-2 p-2">
                                                    {s}
                                                    <button
                                                        type="button"
                                                        className="btn-close btn-close-white"
                                                        style={{ fontSize: '8px' }}
                                                        onClick={() => removeSize(s)}
                                                    ></button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {sizes.length === 0 && (
                                        <small className="text-muted">No sizes added yet. Type a size and press Enter or click "Add Size".</small>
                                    )}
                                </div>
                            )}

                            {/* PRICING GRID - varies by article type */}
                            <h5 className="fw-bold text-secondary mb-3 border-bottom pb-2">💰 Pricing & Costing</h5>
                            <div className="row g-2 mb-4">
                                {showFullPricing ? (
                                    <>
                                        {/* SUIT: full cost breakdown */}
                                        <div className="col-md-2 col-6"><div className="form-floating"><input type="number" className="form-control bg-light border-0" name="fabric" placeholder="Fab" /><label>Fabric</label></div></div>
                                        <div className="col-md-2 col-6"><div className="form-floating"><input type="number" className="form-control bg-light border-0" name="work" placeholder="Work" /><label>Work</label></div></div>
                                        <div className="col-md-2 col-6"><div className="form-floating"><input type="number" className="form-control bg-light border-0" name="stiching" placeholder="Stich" /><label>Stiching</label></div></div>
                                        <div className="col-md-2 col-6"><div className="form-floating"><input type="number" className="form-control bg-light border-0" name="dupatta" placeholder="Dup" /><label>Dupatta</label></div></div>
                                        <div className="col-md-2 col-6"><div className="form-floating"><input type="number" className="form-control bg-light border-0" name="misc" placeholder="Misc" /><label>Misc</label></div></div>
                                        <div className="col-md-2 col-6">
                                            <div className="form-floating">
                                                <input type="number" className="form-control bg-primary text-white border-0" name="salePrice" placeholder="Sale" />
                                                <label className="text-white">Sale Price</label>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* JUTTI / JEWELLERY / BAGS: only cost price (stored as fabric internally) and sale price */}
                                        <div className="col-md-3 col-6">
                                            <div className="form-floating">
                                                <input type="number" className="form-control bg-light border-0" name="fabric" placeholder="Cost" />
                                                <label>Cost Price</label>
                                            </div>
                                        </div>
                                        <div className="col-md-3 col-6">
                                            <div className="form-floating">
                                                <input type="number" className="form-control bg-primary text-white border-0" name="salePrice" placeholder="Sale" />
                                                <label className="text-white">Sale Price</label>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* WEBSITE CONTROLS */}
                            <div className="d-flex gap-4 mb-4 p-3 bg-light rounded">
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="publicToggle" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
                                    <label className="form-check-label fw-bold" htmlFor="publicToggle">Show on Website (Public)</label>
                                </div>
                                <div className="form-check form-switch">
                                    <input className="form-check-input" type="checkbox" id="featureToggle" checked={isFeatured} onChange={(e) => setIsFeatured(e.target.checked)} />
                                    <label className="form-check-label fw-bold" htmlFor="featureToggle">Featured Article</label>
                                </div>
                            </div>

                            {/* PROMOTION ENTRIES */}
                            <div className="mt-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="fw-bold text-secondary mb-0">📢 Promotion History</h6>
                                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={addPromotionField}>+ Add Row</button>
                                </div>
                                
                                {promotionEntries.map((entry, index) => (
                                    <div key={index} className="row g-1 mb-1 align-items-center">
                                        <div className="col-md-2"><input type="date" className="form-control form-control-sm bg-light border-0" value={entry.date} onChange={(e) => handlePromotionChange(index, "date", e.target.value)} /></div>
                                        <div className="col-md-2"><input type="number" className="form-control form-control-sm bg-light border-0" placeholder="Cost" value={entry.cost} onChange={(e) => handlePromotionChange(index, "cost", e.target.value)} /></div>
                                        <div className="col-md-3"><input type="text" className="form-control form-control-sm bg-light border-0" placeholder="Source" value={entry.from} onChange={(e) => handlePromotionChange(index, "from", e.target.value)} /></div>
                                        <div className="col-md-4"><input type="text" className="form-control form-control-sm bg-light border-0" placeholder="Desc" value={entry.description} onChange={(e) => handlePromotionChange(index, "description", e.target.value)} /></div>
                                        <div className="col-md-1 text-end">
                                            <button type="button" className="btn btn-sm text-danger" onClick={() => removePromotionField(index)} disabled={promotionEntries.length === 1}>✖</button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* ACTION BUTTONS */}
                            {updating?(<div className="d-grid gap-2 mt-4">
                                <button className="btn btn-primary opacity-50 py-3 fw-bold shadow-sm" type="submit" disabled= {updating} >Updating........</button>
                                <button type="button" className="btn btn-light text-muted py-2 opacity-25 border-0" disabled= {updating} onClick={() => navigate('/articles')}>Discard Changes</button>
                            </div>):(<div className="d-grid gap-2 mt-4">
                                <button className="btn btn-primary py-3 fw-bold shadow-sm" type="submit">Save Article to Cloud</button>
                                <button type="button" className="btn btn-light text-muted py-2 border-0" onClick={() => navigate('/articles')}>Discard Changes</button>
                            </div>)}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddArticle;